import type { BookingApi } from '$lib/model/booking';
import type { Giftcard } from '$lib/model/giftcard';
import type { UnknownNotification } from '$lib/model/notification';
import type { ApiScan } from '../qr-code/qr-code';

export enum AppEvent {
	USER_LOGGED_IN = 'user_logged_in',
	USER_DO_NOT_DISTURB_UPDATED = 'user_do_not_disturb_updated',
	BUSINESS_UPDATED = 'business_updated',
	BUSINESS_LOGO_UPDATED = 'business_logo_updated',
	BUSINESS_UI_UPDATED = 'business_ui_updated',
	BUSINESS_FEATURE_FLIPPING_UPDATED = 'business_feature_flipping_updated',
	BUSINESS_CREATED = 'business_created',
	BOOKING_CREATED = 'booking_created',
	BOOKING_UPDATED = 'booking_updated',
	BOOKING_INTENT = 'booking_intent',
	BOOKING_INTENT_EXPIRED = 'booking_intent_expired',
	BOOKING_CONFIRMED = 'booking_confirmed',
	BOOKING_CANCELED = 'booking_canceled',
	BOOKING_BOOKED = 'booking_booked',
	BOOKING_REVIEW_ANSWERED = 'booking_review_answered',
	BOOKING_REVIEW_UNREAD_COUNT_UPDATED = 'booking_review_unread_count_updated',
	CUSTOMER_CREATED = 'customer_created',
	CUSTOMER_UPDATED = 'customer_updated',
	GIFTCARD_CREATED = 'giftcard_created',
	GIFTCARD_CONSUMED = 'giftcard_consumed',
	GIFTCARD_DELETED = 'giftcard_deleted',
	GIFTCARD_ORDER_PAYED = 'giftcard_order_payed',
	BOOKABLE_GIFTCARD_CREATED = 'bookable_giftcard_created',
	BOOKABLE_GIFTCARD_UPDATED = 'bookable_giftcard_updated',
	BOOKABLE_GIFTCARD_DELETED = 'bookable_giftcard_deleted',
	OPENING_HOUR_CREATED = 'opening_hour_created',
	OPENING_HOUR_UPDATED = 'opening_hour_updated',
	OPENING_HOUR_DELETED = 'opening_hour_deleted',
	OPENING_HOUR_EXCEPTION_CREATED = 'opening_hour_exception_created',
	OPENING_HOUR_EXCEPTION_UPDATED = 'opening_hour_exception_updated',
	OPENING_HOUR_EXCEPTION_DELETED = 'opening_hour_exception_deleted',
	ACTIVITY_CREATED = 'activity_created',
	ACTIVITY_UPDATED = 'activity_updated',
	ACTIVITY_LOGO_UPDATED = 'activity_logo_updated',
	ACTIVITY_DELETED = 'activity_deleted',
	AREA_CREATED = 'area_created',
	AREA_UPDATED = 'area_updated',
	AREA_LOGO_UPDATED = 'area_logo_updated',
	AREA_DELETED = 'area_deleted',
	SESSION_CREATED = 'session_created',
	SESSION_UPDATED = 'session_updated',
	SESSION_LOGO_UPDATED = 'session_logo_updated',
	SESSION_DELETED = 'session_deleted',
	OFFER_CREATED = 'offer_created',
	OFFER_UPDATED = 'offer_updated',
	OFFER_DELETED = 'offer_deleted',
	OFFER_PUBLISHED = 'offer_published',
	OFFER_UNPUBLISHED = 'offer_unpublished',
	SUBSCRIPTION_SELECTED = 'subscription_selected',
	SUBSCRIPTION_CANCELED = 'subscription_canceled',
	SUBSCRIPTION_REACTIVATED = 'subscription_reactivated',
	VOUCHER_APPLIED = 'voucher_applied',
	PAYMENT_METHOD_UPDATED = 'payment_method_updated',
	NOTIFICATION_CREATED = 'notification_created',
	NOTIFICATION_READ = 'notification_read',
	NOTIFICATION_ALL_READ = 'notification_all_read',
	NOTIFICATION_UNREAD = 'notification_unread',
	SMS_CREDITS_UPDATED = 'business_sms_credits_updated',
	SMS_CREDITS_REFILL_REQUEST_MANUAL = 'business_sms_credits_refill_request_manual',
	SMS_CREDITS_REFILL_REQUEST_AUTO = 'business_sms_credits_refill_request_auto',
	QR_CODE_SCANNED = 'qr_code_scanned'
}

export type UserDoNotDisturbUpdatedEvent = {
	event: AppEvent.USER_DO_NOT_DISTURB_UPDATED;
	payload: {
		until: string | null;
	};
};

export type BookingReviewUnreadCountUpdatedEvent = {
	event: AppEvent.BOOKING_REVIEW_UNREAD_COUNT_UPDATED;
	payload: {
		count: number;
	};
};

export type NotificationCreatedEvent = {
	event: AppEvent.NOTIFICATION_CREATED;
	payload: {
		notification_id: number;
		notification: UnknownNotification;
	};
};
export type NotificationReadEvent = {
	event: AppEvent.NOTIFICATION_READ;
	payload: {
		notification_id: number;
	};
};
export type NotificationAllReadEvent = {
	event: AppEvent.NOTIFICATION_ALL_READ;
	payload: Record<string, never>;
};
export type NotificationUnreadEvent = {
	event: AppEvent.NOTIFICATION_UNREAD;
	payload: {
		notification_id: number;
	};
};

export type GiftcardCreatedEvent = {
	event: AppEvent.GIFTCARD_CREATED;
	payload: {
		giftcard_id: string;
		changes: Partial<Giftcard>;
	};
};
export type GiftcardConsumedEvent = {
	event: AppEvent.GIFTCARD_CONSUMED;
	payload: {
		giftcard_id: string;
		amount: number;
		code: string;
	};
};
export type GiftcardDeletedEvent = {
	event: AppEvent.GIFTCARD_DELETED;
	payload: {
		giftcard_id: string;
	};
};

export type BookingCreateUpdateEvent = {
	event: AppEvent.BOOKING_CREATED | AppEvent.BOOKING_UPDATED;
	payload: {
		booking_id: string;
		changes: Partial<BookingApi>;
	};
};
export type BookingBookedEvent = {
	event: AppEvent.BOOKING_BOOKED;
	payload: {
		booking_id: string;
		offer: string;
		pax: number;
		date: string;
		date_human: string;
		time_human: string;
	};
};
export type BookingCanceledEvent = {
	event: AppEvent.BOOKING_CANCELED;
	payload: {
		booking_id: string;
		offer: string;
		pax: number;
		date: string;
		date_human: string;
		time_human: string;
	};
};
export type BookingReviewAnsweredEvent = {
	event: AppEvent.BOOKING_REVIEW_ANSWERED;
	payload: {
		booking_id: string;
		customer: string;
		date: string;
		date_human: string;
		time_human: string;
	};
};

export type QrCodeScannedEvent = {
	event: AppEvent.QR_CODE_SCANNED;
	payload: ApiScan;
};
